import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api.js";
import { useAsync } from "../lib/hooks.js";
import {
  Card,
  Empty,
  ErrorState,
  Loading,
  PageIntro,
  SeverityBadge,
  Stat,
  formatDateTime,
  formatRelative,
} from "../components/ui.jsx";
import { IconAlert, IconRefresh } from "../components/icons.jsx";

const SEVERITIES = ["CRITICAL", "WARNING", "INFO"];

export default function Alerts() {
  const [severity, setSeverity] = useState("");
  const [device, setDevice] = useState("");

  const { data, error, loading, reload } = useAsync(
    () => api.alerts({ severity, device, limit: 200 }),
    [severity, device]
  );

  const alerts = data ?? [];
  const counts = SEVERITIES.reduce((acc, s) => {
    acc[s] = alerts.filter((a) => a.Severity === s).length;
    return acc;
  }, {});

  return (
    <>
      <PageIntro title="Device events">
        Alerts are raised by devices in the field and stored exactly as reported. Critical
        events usually mean tamper or a safety condition and should be investigated before
        anything else.
      </PageIntro>

      <div className="grid grid-3" style={{ marginBottom: 20 }}>
        <Stat label="Critical" value={counts.CRITICAL ?? 0} sub="Needs intervention" icon={IconAlert} />
        <Stat label="Warning" value={counts.WARNING ?? 0} sub="Needs attention" icon={IconAlert} />
        <Stat label="Informational" value={counts.INFO ?? 0} sub="Retained for audit" icon={IconAlert} />
      </div>

      <div className="toolbar">
        <select className="select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </option>
          ))}
        </select>

        <input
          className="input"
          placeholder="Filter by device number"
          value={device}
          onChange={(e) => setDevice(e.target.value)}
        />

        <span className="spacer" />
        <button className="btn btn-secondary btn-sm" onClick={reload}>
          <IconRefresh /> Refresh
        </button>
      </div>

      {loading ? (
        <Loading label="Loading alerts" />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : alerts.length === 0 ? (
        <Card>
          <Empty title="Nothing reported">
            No devices have raised events matching these filters.
          </Empty>
        </Card>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Severity</th>
                <th>Event</th>
                <th>Device</th>
                <th>Channel</th>
                <th>Message</th>
                <th>Occurred</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.ID}>
                  <td>
                    <SeverityBadge severity={a.Severity} />
                  </td>
                  <td className="mono">{a.EventCode}</td>
                  <td>
                    <Link className="mono" to={`/devices/${a.DeviceNumber}`}>
                      {a.DeviceNumber}
                    </Link>
                  </td>
                  <td>{a.ChannelIndex || "Whole unit"}</td>
                  <td className="hint">{a.Message || "-"}</td>
                  <td className="hint" title={formatDateTime(a.OccurredAt)}>
                    {formatRelative(a.OccurredAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
